import { Auth0Client, createAuth0Client, User } from "@auth0/auth0-spa-js";
import { StorageLike } from "../shared/storage.js";
import { WarningEvent } from "./event.warning.js";
import { dispatch } from "./util.events.js";

export const AUTH0_AUDIENCE = "https://api.readingbee.megazear7.com";
export const PASSCODE_RESET_KEY = "reading-bee:reset-passcode";
export const LAST_INSTRUCTOR_SUB_KEY = "reading-bee:last-instructor-sub";
export const PASSCODE_RESET_TTL_MS = 15 * 60 * 1000;

export type AuthUser = {
  sub: string;
  name?: string;
  email?: string;
};

export type AuthInitResult = {
  returnTo: string | null;
  error: string | null;
  fromCallback: boolean;
  resetPasscode: boolean;
};

const domain = (process.env.AUTH0_DOMAIN ?? "").trim();
const clientId = (process.env.AUTH0_CLIENT_ID ?? "").trim();
const audience = (process.env.AUTH0_AUDIENCE ?? AUTH0_AUDIENCE).trim() || AUTH0_AUDIENCE;

let client: Auth0Client | null = null;
let currentUser: AuthUser | null = null;
let resolveReady: (() => void) | null = null;
const ready = new Promise<void>((resolve) => {
  resolveReady = resolve;
});

export const authSession = new EventTarget();

export const whenAuthReady = (): Promise<void> => ready;

const markReady = (): void => {
  resolveReady?.();
  resolveReady = null;
};

export const isAuthConfigured = (authDomain = domain, authClientId = clientId): boolean => {
  return Boolean(authDomain && authClientId);
};

export const isAuthCallbackSearch = (search: string): boolean => {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return params.has("state") && (params.has("code") || params.has("error"));
};

export const urlAfterAuthCallback = (href: string): string => {
  const url = new URL(href);
  for (const key of ["code", "state", "error", "error_description", "error_uri"]) {
    url.searchParams.delete(key);
  }
  return `${url.pathname}${url.search}${url.hash}`;
};

export const getAuthUser = (): AuthUser | null => currentUser;

const toAuthUser = (user: User | undefined): AuthUser | null => {
  if (!user?.sub) return null;
  return {
    sub: user.sub,
    name: user.name || user.nickname || undefined,
    email: user.email || undefined,
  };
};

const notify = (): void => {
  authSession.dispatchEvent(new Event("change"));
};

const loadUser = async (): Promise<void> => {
  if (!client) {
    currentUser = null;
    return;
  }
  const signedIn = await client.isAuthenticated();
  currentUser = signedIn ? toAuthUser(await client.getUser()) : null;
};

const browserStorage = (): StorageLike | null => {
  if (typeof localStorage === "undefined") return null;
  return localStorage;
};

export const markPasscodeResetPending = (storage: StorageLike | null = browserStorage(), now = Date.now()): void => {
  storage?.setItem(PASSCODE_RESET_KEY, JSON.stringify({ at: now }));
};

const pendingIsFresh = (raw: string | null, now: number): boolean => {
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as { at?: unknown };
    return typeof parsed.at === "number" && now - parsed.at >= 0 && now - parsed.at < PASSCODE_RESET_TTL_MS;
  } catch {
    return false;
  }
};

export const peekPasscodeResetPending = (storage: StorageLike | null = browserStorage(), now = Date.now()): boolean => {
  return pendingIsFresh(storage?.getItem(PASSCODE_RESET_KEY) ?? null, now);
};

export const consumePasscodeResetPending = (
  storage: StorageLike | null = browserStorage(),
  now = Date.now(),
): boolean => {
  if (!storage) return false;
  const raw = storage.getItem(PASSCODE_RESET_KEY);
  storage.removeItem(PASSCODE_RESET_KEY);
  return pendingIsFresh(raw, now);
};

export const peekLastInstructorSub = (storage: StorageLike | null = browserStorage()): string | null => {
  return storage?.getItem(LAST_INSTRUCTOR_SUB_KEY) ?? null;
};

export const rememberInstructorSub = (sub: string, storage: StorageLike | null = browserStorage()): void => {
  storage?.setItem(LAST_INSTRUCTOR_SUB_KEY, sub);
};

export const clearLastInstructorSub = (storage: StorageLike | null = browserStorage()): void => {
  storage?.removeItem(LAST_INSTRUCTOR_SUB_KEY);
};

export const shouldResetPasscodeAfterAuth = (args: {
  fromCallback: boolean;
  resetPasscode: boolean;
  pending: boolean;
  userSub: string | null;
  lastSub: string | null;
}): { reset: boolean; error: string | null } => {
  if (!args.fromCallback || !args.userSub || (!args.resetPasscode && !args.pending)) {
    return { reset: false, error: null };
  }
  if (args.lastSub && args.lastSub !== args.userSub) {
    return {
      reset: false,
      error: "Sign in with the instructor account used on this device to reset the passcode.",
    };
  }
  return { reset: true, error: null };
};

export const initAuth0 = async (): Promise<AuthInitResult> => {
  const result: AuthInitResult = { returnTo: null, error: null, fromCallback: false, resetPasscode: false };
  if (!isAuthConfigured() || typeof window === "undefined") {
    currentUser = null;
    notify();
    markReady();
    return result;
  }
  try {
    client = await createAuth0Client({
      domain,
      clientId,
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience,
        scope: "openid profile email",
      },
      cacheLocation: "localstorage",
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
    });
    if (isAuthCallbackSearch(window.location.search)) {
      result.fromCallback = true;
      try {
        const callback = await client.handleRedirectCallback();
        const appState = callback.appState as { returnTo?: unknown; resetPasscode?: unknown } | undefined;
        result.returnTo = typeof appState?.returnTo === "string" ? appState.returnTo : null;
        result.resetPasscode = appState?.resetPasscode === true;
      } catch (error) {
        result.error = error instanceof Error ? error.message : "Sign-in did not finish";
      }
      const cleaned = urlAfterAuthCallback(window.location.href);
      if (`${window.location.pathname}${window.location.search}${window.location.hash}` !== cleaned) {
        window.history.replaceState(window.history.state, "", cleaned);
      }
    }
    await loadUser();
  } catch (error) {
    client = null;
    currentUser = null;
    result.error = error instanceof Error ? error.message : "Could not start instructor sign-in";
  }
  notify();
  markReady();
  return result;
};

export const loginReturnTo = (pathWithSearch: string): string => {
  const path = pathWithSearch.split("?")[0] || "/";
  if (path === "/login" || path.startsWith("/login/")) {
    return "/";
  }
  if (path.startsWith("/share/") || path.startsWith("/settings/share/")) {
    return pathWithSearch.split("#")[0] || "/";
  }
  return "/";
};

export const loginInstructor = async (returnTo?: string): Promise<void> => {
  if (!client) {
    throw new Error("Instructor sign-in is not configured");
  }
  const dest = loginReturnTo(returnTo ?? `${window.location.pathname}${window.location.search}`);
  await client.loginWithRedirect({
    appState: { returnTo: dest },
  });
};

export const loginInstructorForPasscodeReset = async (): Promise<void> => {
  if (!client) {
    throw new Error("Instructor sign-in is not configured");
  }
  markPasscodeResetPending();
  await client.loginWithRedirect({
    authorizationParams: {
      prompt: "login",
      max_age: 0,
    },
    appState: { returnTo: "/settings", resetPasscode: true },
  });
};

export const recoverPasscodeWithAuth0 = async (host: HTMLElement): Promise<void> => {
  if (!isAuthConfigured()) {
    dispatch(host, WarningEvent("Sign-in is not set up, so the passcode cannot be reset this way."));
    return;
  }
  try {
    await loginInstructorForPasscodeReset();
  } catch {
    dispatch(host, WarningEvent("Could not start sign-in"));
  }
};

export const logoutInstructor = async (): Promise<void> => {
  if (!client) return;
  clearLastInstructorSub();
  await client.logout({
    logoutParams: { returnTo: window.location.origin },
  });
};

export type AuthTokenProblem = "reauthenticate" | "network" | "configuration" | "unknown";

export class AuthTokenError extends Error {
  readonly problem: AuthTokenProblem;
  readonly detail: unknown;

  constructor(problem: AuthTokenProblem, message: string, options?: { cause?: unknown }) {
    super(message);
    this.name = "AuthTokenError";
    this.problem = problem;
    this.detail = options?.cause;
  }
}

const REAUTHENTICATE_CODES = new Set([
  "login_required",
  "consent_required",
  "interaction_required",
  "account_selection_required",
  "invalid_grant",
  "missing_refresh_token",
  "expired_token",
]);

const CONFIGURATION_CODES = new Set([
  "unauthorized_client",
  "invalid_client",
  "invalid_request",
  "unsupported_grant_type",
]);

const isBrowserOffline = (): boolean => typeof navigator !== "undefined" && navigator.onLine === false;

export const classifyTokenError = (error: unknown): AuthTokenError => {
  const code = typeof (error as { error?: unknown })?.error === "string" ? (error as { error: string }).error : "";
  const description =
    typeof (error as { error_description?: unknown })?.error_description === "string"
      ? (error as { error_description: string }).error_description
      : error instanceof Error
        ? error.message
        : "";
  if (REAUTHENTICATE_CODES.has(code)) {
    return new AuthTokenError(
      "reauthenticate",
      "Your instructor sign-in has expired. Please sign in again to keep syncing profiles.",
      { cause: error },
    );
  }
  if (code === "mfa_required") {
    return new AuthTokenError("reauthenticate", "Please sign in again to finish verifying your instructor account.", {
      cause: error,
    });
  }
  if (isBrowserOffline() || (error instanceof TypeError && /fetch|network/i.test(error.message))) {
    return new AuthTokenError(
      "network",
      "Reading Bee could not reach the sign-in service. Updates are saved on this device and will sync once the connection is back.",
      { cause: error },
    );
  }
  if (code === "timeout") {
    // The silent-refresh iframe times out when the browser blocks third-party sign-in cookies; signing in again fixes it.
    return new AuthTokenError(
      "reauthenticate",
      "Your instructor sign-in could not be refreshed in the background. Please sign in again to keep syncing profiles.",
      { cause: error },
    );
  }
  if (CONFIGURATION_CODES.has(code) || /service not found|audience/i.test(description)) {
    return new AuthTokenError(
      "configuration",
      `Instructor sign-in is not set up correctly for this site${description ? ` (${description})` : ""}. Signing in again will not fix this; the app's Auth0 settings need to be updated.`,
      { cause: error },
    );
  }
  return new AuthTokenError(
    "unknown",
    `Reading Bee could not confirm your instructor sign-in${description ? `: ${description}` : ""}. Try signing in again.`,
    { cause: error },
  );
};

export const getAccessToken = async (): Promise<string | null> => {
  if (!client) return null;
  try {
    return await client.getTokenSilently({
      authorizationParams: { audience },
    });
  } catch (error) {
    const classified = classifyTokenError(error);
    console.warn(`Reading Bee access token unavailable (${classified.problem})`, error);
    throw classified;
  }
};

export const getIdToken = async (): Promise<string | null> => {
  if (!client) return null;
  try {
    const claims = await client.getIdTokenClaims();
    const raw = claims?.__raw;
    return typeof raw === "string" && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
};
